import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { IInventoryLog } from "../../types/inventoryLog.type";
import useGetRequest from "../../hooks/useGetRequest";
import {
  Button,
  Dropdown,
  Form,
  Image,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Popconfirm,
  Select,
  Table,
  Typography,
} from "antd";
import { api } from "../../api/api";
import { Currency } from "../../types/currency.type";
import { DownOutlined } from "@ant-design/icons";
import { API } from "../../config/config";

const { Title } = Typography;

export default function InventoryLogs() {
  const { id, name } = useParams();
  const [logs, setLogs] = useState<IInventoryLog[]>([]);
  const { data, loading, error, refresh } = useGetRequest(
    `/inventory/logs/${id}`
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (data) {
      setLogs(data.logs);
    }
  }, [data]);

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/inventory/log/${id}`);
      message.success("Журнал успешно удален.");
      refresh();
    } catch (error: any) {
      message.error(error.response.data.error);
    }
  };

  const handleSubmit = async (values: Partial<IInventoryLog>) => {
    setConfirmLoading(true);

    const formData = new FormData();

    Object.keys(values).forEach((key) => {
      const fieldKey = key as keyof IInventoryLog;
      if (values[fieldKey] !== undefined) {
        formData.append(fieldKey as string, values[fieldKey] as any);
      }
    });

    if (file) {
      formData.append("photo", file);
    }

    try {
      await api.post(`/inventory/log/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      message.success("Журнал успешно добавлен.");
      setIsModalOpen(false);
      setConfirmLoading(false);
      refresh();
      form.resetFields();
      setFile(null);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving item:", error);
      message.error(error.response.data.error || "Ошибка.");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleCustomButtonClick = () => {
    fileInputRef.current?.click();
  };

  const columns = [
    {
      title: "Трек ID",
      dataIndex: "childTrackingId",
      key: "childTrackingId",
    },
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Фото",
      key: "photoUri",
      dataIndex: "photoUri",
      render: (photoUri: string) => {
        if (!photoUri) return "Нет";

        console.log("Image path:", `${API}/${photoUri}`);

        return (
          <div>
            <Image
              key={photoUri}
              src={`${API}/${photoUri}`}
              width={50}
              height={50}
              alt="Фото"
              style={{
                cursor: "pointer",
                borderRadius: 4,
                border: "1px solid #ddd",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
              }}
            />
          </div>
        );
      },
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Количество",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Цена",
      dataIndex: "price",
      key: "price",
    },
    {
      title: "Общая стоимость",
      dataIndex: "total",
      key: "total",
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Курс",
      dataIndex: "exchangeRate",
      key: "exchangeRate",
    },
    {
      title: "Добавил",
      dataIndex: "ownerName",
      key: "ownerName",
    },
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: IInventoryLog) => {
        const menu = (
          <Menu>
            <Menu.Item>
              <Popconfirm
                title="Вы уверены, что хотите удалить этот журнал?"
                onConfirm={() => handleDelete(record._id)}
                okText="Да"
                cancelText="Нет"
              >
                Удалить
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Журнал {name}</Title>
      <Button type="primary" onClick={() => setIsModalOpen(true)}>
        Добавить
      </Button>
      <Table
        loading={loading}
        columns={columns}
        dataSource={logs}
        rowKey={(record) => record._id}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Добавить в журнал"
        open={isModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Название"
            rules={[
              { required: true, message: "Пожалуйста, введите название" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[
              { required: true, message: "Пожалуйста, введите описание" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="quantity"
            label="Кол-во"
            rules={[
              { required: true, message: "Пожалуйста, введите количество" },
            ]}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="price"
            label="Цена"
            rules={[{ required: true, message: "Пожалуйста, введите цену" }]}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: "Пожалуйста, выберете валюту" }]}
          >
            <Select allowClear style={{ width: 200 }}>
              {Object.values(Currency).map((currency) => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="exchangeRate"
            label="Курс"
            rules={[{ required: true, message: "Пожалуйста, введите курс" }]}
          >
            <InputNumber />
          </Form.Item>

          <Form.Item name="photo" label="Фото" valuePropName="file">
            <Button onClick={handleCustomButtonClick} type="default">
              Выбрать фото
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {file && (
              <div style={{ marginTop: 10 }}>
                <img
                  src={URL.createObjectURL(file)}
                  alt="Selected"
                  style={{ width: 100, height: 100, objectFit: "cover" }}
                />
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
